import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { FiCheckCircle, FiXCircle, FiRefreshCw } from 'react-icons/fi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useAppDispatch } from '@/store/hooks'
import { verifyEmail, resendVerification } from '@/store/slices/authSlice'

interface VerifyEmailState {
    email?: string
    verificationCode?: string | null
}

const VerifyEmailPage: React.FC = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const locationState = (location.state as VerifyEmailState) || {}

    const [email, setEmail] = useState(locationState.email || '')
    const [code, setCode] = useState(locationState.verificationCode || '')
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
    const [errorMessage, setErrorMessage] = useState('')
    const [isResending, setIsResending] = useState(false)
    const [codeSent, setCodeSent] = useState(false)

    useEffect(() => {
        if (status === 'success') {
            const timeout = setTimeout(() => {
                navigate('/dashboard', {
                    replace: true
                })
            }, 2000)
            return () => clearTimeout(timeout)
        }
    }, [status, navigate])

    useEffect(() => {
        const sendVerificationCode = async () => {
            if (!codeSent && email) {
                try {
                    await dispatch(resendVerification()).unwrap()
                    setCodeSent(true)
                } catch (error) {
                    console.error('Failed to send verification code:', error)
                }
            }
        }

        sendVerificationCode()
    }, [email, codeSent, dispatch])

    const handleCodeChange = (value: string) => {
        const sanitized = value.replace(/\D/g, '').slice(0, 6)
        setCode(sanitized)
    }

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()
        const trimmedEmail = email.trim()

        if (!trimmedEmail) {
            setErrorMessage('Email is required')
            setStatus('error')
            return
        }

        if (code.length !== 6) {
            setErrorMessage('Please enter the 6-digit verification code')
            setStatus('error')
            return
        }

        setStatus('submitting')
        setErrorMessage('')

        try {
            await dispatch(verifyEmail({ email: trimmedEmail.toLowerCase(), code })).unwrap()
            setStatus('success')
        } catch (error: any) {
            setStatus('error')
            setErrorMessage(error || 'Email verification failed. Please try again.')
        }
    }

    const handleResendCode = async () => {
        const trimmedEmail = email.trim()
        
        if (!trimmedEmail) {
            setErrorMessage('Please enter your email address first')
            setStatus('error')
            return
        }

        setIsResending(true)
        setErrorMessage('')
        
        try {
            const response = await dispatch(resendVerification()).unwrap()
            if (response?.verificationCode) {
                setCode(response.verificationCode)
            }
        } catch (error: any) {
        } finally {
            setIsResending(false)
        }
    }

    return (
        <>
            <Helmet>
                <title>Verify Email - SideGO</title>
                <meta name="description" content="Verify your email address to activate your SideGO account." />
            </Helmet>

            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <div className="text-center">
                        <div className="flex justify-center">
                            <div className="h-12 w-12 bg-primary-600 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-2xl">S</span>
                            </div>
                        </div>
                        <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white">
                            Verify your email
                        </h2>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                            Enter the 6-digit code we emailed you to post gigs, apply for jobs, and unlock every SideGO feature.
                        </p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-8">
                        {status === 'success' ? (
                            <div className="text-center">
                                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900 mb-6">
                                    <FiCheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                    Email verified successfully!
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400 mb-6">
                                    Redirecting you to your dashboard...
                                </p>
                                <Link
                                    to="/dashboard"
                                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                >
                                    Go to dashboard
                                </Link>
                            </div>
                        ) : (
                            <form className="space-y-6" onSubmit={handleSubmit}>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Email address
                                    </label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(event) => setEmail(event.target.value)}
                                        className="appearance-none rounded-md relative block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-400 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700"
                                        placeholder="you@example.com"
                                        required
                                        autoComplete="email"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Verification code
                                    </label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        value={code}
                                        onChange={(event) => handleCodeChange(event.target.value)}
                                        className="tracking-[0.5em] text-center appearance-none rounded-md relative block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-400 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-lg dark:bg-gray-700"
                                        placeholder="123456"
                                        maxLength={6}
                                    />
                                    {locationState.verificationCode && (
                                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                            Development hint: {locationState.verificationCode}
                                        </p>
                                    )}
                                </div>

                                {status === 'error' && errorMessage && (
                                    <div className="flex items-start space-x-2 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 px-4 py-3 rounded-md">
                                        <FiXCircle className="mt-0.5 h-5 w-5" />
                                        <p className="text-sm">{errorMessage}</p>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={status === 'submitting'}
                                    className="w-full flex justify-center items-center px-4 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-60"
                                >
                                    {status === 'submitting' ? (
                                        <>
                                            <LoadingSpinner className="h-5 w-5 mr-2" />
                                            Verifying...
                                        </>
                                    ) : (
                                        'Verify email'
                                    )}
                                </button>

                                <div className="text-sm text-center text-gray-600 dark:text-gray-400">
                                    Didn't receive the code? Check your spam folder or{' '}
                                    <button
                                        type="button"
                                        onClick={handleResendCode}
                                        disabled={isResending || status === 'submitting'}
                                        className="text-primary-600 hover:text-primary-500 font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                                    >
                                        {isResending ? (
                                            <>
                                                <FiRefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                                Sending...
                                            </>
                                        ) : (
                                            'resend the code'
                                        )}
                                    </button>
                                </div>

                                <div className="text-center text-sm">
                                    <Link to="/login" className="text-primary-600 hover:text-primary-500">
                                        Back to login
                                    </Link>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}

export default VerifyEmailPage
